import React, { Component } from 'react'
import { toast } from 'react-toastify'
import { getString } from '../../../locale'
import Loader from '../../../components/loader'
import ToggleSwitch from '../../../components/toggle-switch'
import {
    getBluetoothStatus,
    enableBluetooth,
    getBluetoothDeviceName,
    setBluetoothDeviceName,
    startBluetoothAdvertising,
    stopBluetoothAdvertising,
    BluetoothStatusResponse,
} from '../../../api/0x0005/bluetooth'

type Props = {
    clientId: string
}

type State = {
    loading: boolean
    status?: BluetoothStatusResponse
    showEditModal: boolean
    editValue: string
}

export default class BluetoothTab extends Component<Props, State> {
    state: State = {
        loading: true,
        showEditModal: false,
        editValue: '',
    }

    componentDidMount() {
        this.loadBluetoothStatus()
    }

    loadBluetoothStatus = async () => {
        this.setState({ loading: true })
        try {
            const status = await getBluetoothStatus(this.props.clientId)
            this.setState({ status, loading: false })
        } catch (error: any) {
            toast.error(error.message || getString('bluetooth.error'))
            this.setState({ loading: false })
        }
    }

    handleEnableToggle = async () => {
        if (!this.state.status) return

        const newEnabledState = this.state.status.enabled !== 1
        try {
            await enableBluetooth(this.props.clientId, newEnabledState)
            toast.success(
                newEnabledState
                    ? getString('bluetooth.bluetooth_enabled')
                    : getString('bluetooth.bluetooth_disabled')
            )
            await this.loadBluetoothStatus()
        } catch (error: any) {
            toast.error(error.message || getString('bluetooth.error'))
        }
    }

    handleEditDeviceName = () => {
        if (!this.state.status) return
        this.setState({
            showEditModal: true,
            editValue: '', // Start empty since we only have full device name, not customer key
        })
    }

    handleCancelEdit = () => {
        this.setState({ showEditModal: false, editValue: '' })
    }

    handleSaveDeviceName = async () => {
        const newName = this.state.editValue.trim()

        if (newName.length < 1 || newName.length > 20) {
            toast.error(getString('bluetooth.name_length_error'))
            return
        }

        try {
            await setBluetoothDeviceName(this.props.clientId, newName)
            toast.success(getString('bluetooth.device_name_updated'))
            this.setState({ showEditModal: false, editValue: '' })
            await this.loadBluetoothStatus()
        } catch (error: any) {
            toast.error(error.message || getString('bluetooth.error'))
        }
    }

    handleStartAdvertising = async () => {
        try {
            await startBluetoothAdvertising(this.props.clientId)
            toast.success(getString('bluetooth.advertising_started'))
            await this.loadBluetoothStatus()
        } catch (error: any) {
            toast.error(error.message || getString('bluetooth.error'))
        }
    }

    handleStopAdvertising = async () => {
        try {
            await stopBluetoothAdvertising(this.props.clientId)
            toast.success(getString('bluetooth.advertising_stopped'))
            await this.loadBluetoothStatus()
        } catch (error: any) {
            toast.error(error.message || getString('bluetooth.error'))
        }
    }

    render() {
        const { loading, status, showEditModal, editValue } = this.state

        if (loading || !status) {
            return (
                <div className="flex justify-center items-center h-64">
                    <Loader />
                </div>
            )
        }

        return (
            <div className="p-6">
                <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">{getString('bluetooth.title')}</h2>

                {/* Enable/Disable Toggle */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{getString('bluetooth.enable')}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                {status.enabled === 1
                                    ? getString('bluetooth.enabled')
                                    : getString('bluetooth.disabled')}
                            </p>
                        </div>
                        <ToggleSwitch
                            checked={status.enabled === 1}
                            onChange={this.handleEnableToggle}
                        />
                    </div>
                </div>

                {/* Status Card */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">{getString('bluetooth.status')}</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">{getString('common.status')}:</span>
                            <span className={status.isConnected === 1 ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}>
                                {status.isConnected === 1
                                    ? getString('bluetooth.connected')
                                    : getString('bluetooth.not_connected')}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">{getString('bluetooth.advertising')}:</span>
                            <span className={status.isAdvertising === 1 ? 'text-blue-600 dark:text-blue-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}>
                                {status.isAdvertising === 1
                                    ? getString('bluetooth.advertising')
                                    : getString('bluetooth.not_advertising')}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Device Name Card (Read-only) */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">{getString('bluetooth.device_name')}</h3>
                    <div className="text-gray-800 dark:text-gray-200 font-medium">{status.deviceName || 'N/A'}</div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        This name is automatically generated from the customer key
                    </p>
                </div>

                {/* Customer Key Card (Editable) */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">{getString('bluetooth.customer_key')}</h3>
                    <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-400 text-sm">Edit the customer key to update the device name</span>
                        <button
                            onClick={this.handleEditDeviceName}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                            disabled={status.enabled !== 1}
                        >
                            {getString('bluetooth.edit_customer_key')}
                        </button>
                    </div>
                </div>

                {/* MAC Address Card */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">{getString('bluetooth.mac_address')}</h3>
                    <div className="text-gray-800 dark:text-gray-200 font-mono">{status.macAddress || 'N/A'}</div>
                </div>

                {/* Advertising Controls */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">{getString('bluetooth.advertising')}</h3>
                    <div className="flex gap-4">
                        <button
                            onClick={this.handleStartAdvertising}
                            className="flex-1 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                            disabled={status.enabled !== 1 || status.isAdvertising === 1}
                        >
                            {getString('bluetooth.start_advertising')}
                        </button>
                        <button
                            onClick={this.handleStopAdvertising}
                            className="flex-1 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                            disabled={status.enabled !== 1 || status.isAdvertising !== 1}
                        >
                            {getString('bluetooth.stop_advertising')}
                        </button>
                    </div>
                </div>

                {/* Edit Device Name Modal */}
                {showEditModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96">
                            <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">{getString('bluetooth.edit_customer_key')}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                Enter a customer key (1-20 characters). This will be appended to the device name prefix.
                            </p>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {getString('bluetooth.new_customer_key')}
                                </label>
                                <input
                                    type="text"
                                    value={editValue}
                                    onChange={(e) => this.setState({ editValue: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    maxLength={20}
                                    placeholder="Customer key (1-20 chars)"
                                    autoFocus
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {editValue.length}/20 characters
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={this.handleCancelEdit}
                                    className="flex-1 px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                                >
                                    {getString('common.cancel')}
                                </button>
                                <button
                                    onClick={this.handleSaveDeviceName}
                                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                                    disabled={editValue.trim().length < 1 || editValue.trim().length > 20}
                                >
                                    {getString('common.save')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }
}
